if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.updateThumbnailOverflowFade = this.updateThumbnailOverflowFade.bind(this);
        this.updateThumbnailStripHeight = this.updateThumbnailStripHeight.bind(this);
        this.onThumbnailListScroll = this.onThumbnailListScroll.bind(this);
        this.onWindowResize = debounce(() => {
          this.updateThumbnailStripHeight();
          this.updateThumbnailOverflowFade();
        }, 80);

        // Dot indicators (mobile)
        this.dotIndicators = Array.from(
          this.querySelectorAll('.product__media-dots .slider-counter__link')
        );

        if (this.dotIndicators.length) {
          this.dotIndicators.forEach((dot, index) => {
            dot.addEventListener('click', () => this.onDotClick(index));
          });
        }

        if (!this.elements.thumbnails) {
          // Still listen for slide changes to sync dots even without thumbnails
          if (this.dotIndicators.length) {
            this.elements.viewer.addEventListener(
              'slideChanged',
              debounce(this.onSlideChanged.bind(this), 500)
            );
          }
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', () => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false, mediaToSwitch);
          });
        });
        this.syncInitialThumbnailState();
        this.initThumbnailOverflowFade();
        this.initThumbnailStripHeightSync();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onDotClick(index) {
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (slides[index]) {
          slides[index].parentElement.scrollTo({
            left: slides[index].offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
        this.updateDotIndicators(event.detail.currentPage);
      }

      updateDotIndicators(page) {
        if (!this.dotIndicators.length) return;
        this.dotIndicators.forEach((dot, i) => {
          dot.classList.toggle('slider-counter__link--active', i === page - 1);
          if (i === page - 1) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId, prepend, sourceThumbnail = null) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const layout = this.dataset.desktopLayout;
        const isThumbnailLayout = layout && layout.includes('thumbnail');

        if (isThumbnailLayout && this.mql.matches) {
          // For thumbnail-based layouts: don't reorder DOM, just switch active state
          // CSS handles the crossfade / display toggle
        } else if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const isDesktopVerticalThumbnailStrip = this.dataset.desktopLayout === 'thumbnail_left' && this.mql.matches;
          if (isDesktopVerticalThumbnailStrip) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Update dot indicators
        const allSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const activeIndex = allSlides.indexOf(activeMedia);
        if (activeIndex !== -1) this.updateDotIndicators(activeIndex + 1);

        if (!this.elements.thumbnails) return;
        const activeThumbnail =
          sourceThumbnail ||
          this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item')
          .forEach((element) => element.classList.remove('is-active-thumb'));
        thumbnail.classList.add('is-active-thumb');

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', 'true');

        // For vertical thumbnail_left layout, scroll vertically
        if (this.dataset.desktopLayout === 'thumbnail_left' && this.mql.matches) {
          const thumbList = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbList) {
            const currentScrollTop = thumbList.scrollTop;
            const visibleTop = currentScrollTop;
            const visibleBottom = currentScrollTop + thumbList.clientHeight;
            const thumbTop = thumbnail.offsetTop;
            const thumbBottom = thumbTop + thumbnail.offsetHeight;
            const edgeBuffer = 6;
            let nextScrollTop = null;

            if (thumbTop < visibleTop + edgeBuffer) {
              nextScrollTop = Math.max(0, thumbTop - edgeBuffer);
            } else if (thumbBottom > visibleBottom - edgeBuffer) {
              nextScrollTop = Math.max(0, thumbBottom - thumbList.clientHeight + edgeBuffer);
            }

            if (nextScrollTop !== null) {
              thumbList.scrollTo({
                top: nextScrollTop,
                behavior: 'smooth',
              });
              window.setTimeout(() => {
                this.updateThumbnailStripHeight();
                this.updateThumbnailOverflowFade();
              }, 120);
            } else {
              this.updateThumbnailStripHeight();
              this.updateThumbnailOverflowFade();
            }
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      syncInitialThumbnailState() {
        if (!this.elements.thumbnails) return;
        const currentThumbnailButton = this.elements.thumbnails.querySelector('.thumbnail[aria-current]');
        const currentThumbnailItem = currentThumbnailButton?.closest('.thumbnail-list__item');
        if (!currentThumbnailItem) return;

        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item')
          .forEach((element) => element.classList.remove('is-active-thumb'));
        currentThumbnailItem.classList.add('is-active-thumb');
      }

      initThumbnailOverflowFade() {
        if (!this.elements.thumbnails) return;
        this.thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!this.thumbnailList) return;

        if (!this._thumbnailFadeEventsBound) {
          this.thumbnailList.addEventListener('scroll', this.onThumbnailListScroll, { passive: true });
          window.addEventListener('resize', this.onWindowResize, { passive: true });

          const onMqlChange = () => {
            this.updateThumbnailStripHeight();
            this.updateThumbnailOverflowFade();
          };
          this._onThumbnailFadeMqlChange = onMqlChange;
          if (this.mql.addEventListener) {
            this.mql.addEventListener('change', onMqlChange);
          } else if (this.mql.addListener) {
            this.mql.addListener(onMqlChange);
          }

          this._thumbnailFadeEventsBound = true;
        }

        this.updateThumbnailStripHeight();
        this.updateThumbnailOverflowFade();
      }

      initThumbnailStripHeightSync() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        if ('ResizeObserver' in window && !this._thumbnailStripResizeObserver) {
          this._thumbnailStripResizeObserver = new ResizeObserver(() => {
            this.updateThumbnailStripHeight();
            this.updateThumbnailOverflowFade();
          });
          this._thumbnailStripResizeObserver.observe(this.elements.viewer);
        }

        this.updateThumbnailStripHeight();
      }

      onThumbnailListScroll() {
        this.updateThumbnailOverflowFade();
      }

      updateThumbnailOverflowFade() {
        if (!this.elements.thumbnails || !this.thumbnailList) return;

        const isVerticalThumbnailStrip = this.dataset.desktopLayout === 'thumbnail_left' && this.mql.matches;
        if (!isVerticalThumbnailStrip) {
          this.elements.thumbnails.classList.remove('has-thumb-overflow-top', 'has-thumb-overflow-bottom');
          return;
        }

        const maxScrollTop = this.thumbnailList.scrollHeight - this.thumbnailList.clientHeight;
        if (maxScrollTop <= 2) {
          this.elements.thumbnails.classList.remove('has-thumb-overflow-top', 'has-thumb-overflow-bottom');
          return;
        }

        const scrollTop = this.thumbnailList.scrollTop;
        const hasTop = scrollTop > 2;
        const hasBottom = scrollTop < maxScrollTop - 2;

        this.elements.thumbnails.classList.toggle('has-thumb-overflow-top', hasTop);
        this.elements.thumbnails.classList.toggle('has-thumb-overflow-bottom', hasBottom);
      }

      updateThumbnailStripHeight() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        const isVerticalThumbnailStrip = this.dataset.desktopLayout === 'thumbnail_left' && this.mql.matches;
        if (!isVerticalThumbnailStrip) {
          this.elements.thumbnails.style.removeProperty('--sa-thumbnail-strip-max-height');
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active') ||
          this.elements.viewer.querySelector('[data-media-id]');

        let targetHeight = 0;
        if (activeMedia) {
          targetHeight = activeMedia.getBoundingClientRect().height;
        }

        if (!targetHeight) {
          targetHeight = this.elements.viewer.getBoundingClientRect().height;
        }

        if (targetHeight > 0) {
          this.elements.thumbnails.style.setProperty('--sa-thumbnail-strip-max-height', `${Math.round(targetHeight)}px`);
        }
      }
    }
  );
}
